import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  PlaceOutlined as PlaceIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  EditOutlined as EditIcon,
} from "@mui/icons-material";
import {
  dt,
  accentGradientDark,
  accentGradientDarkHover,
  accentGradientLight,
} from "../../../../shared/themes/redesignTokens";
import {
  FormFieldLabel,
  FormTextField,
  FormSelect,
} from "../../../../shared/components/redesign/forms";
import type { FormSelectOption } from "../../../../shared/components/redesign/forms";
import type { Location, LocationFormData } from "./locationTypes";

// ═══════════════════════════════════════════════════════════════
//  PROPS
// ═══════════════════════════════════════════════════════════════

interface EditLocationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: LocationFormData) => void;
  location: Location;
  parentOptions: { value: string; label: string }[];
  countryOptions?: FormSelectOption[];
}

// ═══════════════════════════════════════════════════════════════
//  DEFAULT COUNTRY OPTIONS (placeholder — Machinery Dev replaces)
// ═══════════════════════════════════════════════════════════════

const DEFAULT_COUNTRY_OPTIONS: FormSelectOption[] = [
  { value: 1, label: "Czechia" },
  { value: 2, label: "Slovakia" },
  { value: 3, label: "Germany" },
  { value: 4, label: "Austria" },
  { value: 5, label: "Poland" },
];

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════

const EditLocationDialog: React.FC<EditLocationDialogProps> = ({
  open,
  onClose,
  onSave,
  location,
  parentOptions,
  countryOptions = DEFAULT_COUNTRY_OPTIONS,
}) => {
  // Form state (pre-populated from location)
  const [name, setName] = useState("");
  const [parentLocationId, setParentLocationId] = useState<string>("");
  const [description, setDescription] = useState("");
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [countryId, setCountryId] = useState<number | "">("");
  const [nameError, setNameError] = useState(false);

  // Pre-populate on open
  useEffect(() => {
    if (open && location) {
      setName(location.name);
      setParentLocationId(location.parentLocationId || "");
      setDescription(location.description || "");
      setAddressLine1(location.addressLine1 || "");
      setAddressLine2(location.addressLine2 || "");
      setCity(location.city || "");
      setZipCode(location.zipCode || "");
      setCountryId(location.countryId || "");
      setNameError(false);
      // Auto-expand address section if any address fields have values
      const hasAddress = location.addressLine1 || location.city || location.zipCode || location.countryId;
      setAddressExpanded(!!hasAddress);
    }
  }, [open, location]);

  const handleSave = useCallback(() => {
    if (!name.trim()) {
      setNameError(true);
      return;
    }
    onSave({
      name: name.trim(),
      parentLocationId: parentLocationId || undefined,
      description: description.trim() || undefined,
      addressLine1: addressLine1.trim() || undefined,
      addressLine2: addressLine2.trim() || undefined,
      city: city.trim() || undefined,
      zipCode: zipCode.trim() || undefined,
      countryId: countryId ? Number(countryId) : undefined,
    });
  }, [name, parentLocationId, description, addressLine1, addressLine2, city, zipCode, countryId, onSave]);

  const parentSelectOptions: FormSelectOption[] = parentOptions.map((p) => ({
    value: p.value,
    label: p.label,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          overflow: "hidden",
          animation: "dialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          fontFamily: dt.font,
          fontSize: "18px",
          fontWeight: 700,
          color: dt.textPrimary,
          pb: 1.5,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: dt.blueSoft,
          }}
        >
          <EditIcon sx={{ fontSize: 18, color: dt.blue }} />
        </Box>
        Edit Location
      </DialogTitle>

      {/* Blue accent bar */}
      <Box
        sx={{
          height: "2px",
          mx: 3,
          background: accentGradientLight,
          borderRadius: "1px",
        }}
      />

      {/* Content */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Location Name */}
          <Box>
            <FormFieldLabel required>Location Name</FormFieldLabel>
            <FormTextField
              value={name}
              onChange={(v) => {
                setName(v);
                if (v.trim()) setNameError(false);
              }}
              placeholder="e.g. Main Factory, Hall A"
              error={nameError}
              helperText={nameError ? "Name is required" : undefined}
              maxLength={200}
            />
          </Box>

          {/* Parent Location */}
          <Box>
            <FormFieldLabel>Parent Location</FormFieldLabel>
            <FormSelect
              value={parentLocationId}
              onChange={(v) => setParentLocationId(String(v))}
              options={parentSelectOptions}
              placeholder="None (top-level facility)"
            />
            <Box
              sx={{
                fontSize: "12px",
                fontStyle: "italic",
                color: dt.textMuted,
                mt: 0.5,
                fontFamily: dt.font,
              }}
            >
              Optional. Leave empty to create a top-level facility.
            </Box>
          </Box>

          {/* Description */}
          <Box>
            <FormFieldLabel>Description</FormFieldLabel>
            <FormTextField
              value={description}
              onChange={setDescription}
              placeholder="e.g. New assembly line wing"
              maxLength={500}
            />
          </Box>

          {/* ═══════════════════════════════════════════════════
              ADDRESS DETAILS (collapsible)
              ═══════════════════════════════════════════════════ */}
          <Box sx={{ mt: 1 }}>
            <Box
              onClick={() => setAddressExpanded(!addressExpanded)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { opacity: 0.8 },
              }}
            >
              {addressExpanded ? (
                <ExpandMoreIcon sx={{ fontSize: 16, color: dt.textSecondary }} />
              ) : (
                <ChevronRightIcon sx={{ fontSize: 16, color: dt.textSecondary }} />
              )}
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                  color: dt.textSecondary,
                  fontFamily: dt.font,
                }}
              >
                ADDRESS DETAILS
              </Box>
              <Box
                sx={{
                  flex: 1,
                  height: "1px",
                  backgroundColor: dt.cardBorder,
                  mx: 1,
                }}
              />
              <Box
                sx={{
                  fontSize: "12px",
                  fontStyle: "italic",
                  color: dt.textMuted,
                  fontFamily: dt.font,
                }}
              >
                Optional
              </Box>
            </Box>

            <Collapse in={addressExpanded} timeout={250}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  pt: 2,
                }}
              >
                {/* Address */}
                <Box>
                  <FormFieldLabel>Address</FormFieldLabel>
                  <FormTextField
                    value={addressLine1}
                    onChange={setAddressLine1}
                    placeholder="Street address"
                  />
                </Box>

                {/* City + ZIP in a row */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 2 }}>
                    <FormFieldLabel>City</FormFieldLabel>
                    <FormTextField
                      value={city}
                      onChange={setCity}
                      placeholder="City"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormFieldLabel>ZIP Code</FormFieldLabel>
                    <FormTextField
                      value={zipCode}
                      onChange={setZipCode}
                      placeholder="ZIP"
                    />
                  </Box>
                </Box>

                {/* Country */}
                <Box>
                  <FormFieldLabel>Country</FormFieldLabel>
                  <FormSelect
                    value={countryId}
                    onChange={(v) => setCountryId(v as number)}
                    options={countryOptions}
                    placeholder="Select country"
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Button
          variant="text"
          onClick={onClose}
          sx={{
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "14px",
            textTransform: "none",
            color: dt.textSecondary,
            borderRadius: dt.radiusSm,
            px: 2.5,
            py: 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: dt.inputBg,
              color: dt.textPrimary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<PlaceIcon sx={{ fontSize: "16px !important" }} />}
          onClick={handleSave}
          sx={{
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "13px",
            textTransform: "none",
            borderRadius: dt.radiusPill,
            padding: "8px 24px",
            background: accentGradientDark,
            color: "#fff",
            boxShadow: "0 2px 8px rgba(52,152,219,0.18)",
            transition: "all 0.3s ease",
            "&:hover": {
              background: accentGradientDarkHover,
              boxShadow: "0 4px 14px rgba(52,152,219,0.35)",
              transform: "translateY(-1px)",
            },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLocationDialog;
